// Transport adapter — peels the standard backend envelope to the data payload.
// Sole responsibility: strip `{ data: … }` / `{ success, data: … }` layers
// (the SaasKit ok() envelope, up to 4 deep defensive) and STOP at the envelope
// boundary: once a `{ success, data }` layer is consumed, everything below it
// is domain payload — a domain field named `data` (Notification.data) must
// survive. NOTHING else is peeled here — there is no collection/singleton key
// guessing: a heuristic peeler silently drops sibling fields ({job, progress}
// → job) and lets a new backend sibling flip a fetcher's return type. Instead,
// every endpoint's post-envelope wire shape is declared at the apiFetch generic
// and unwrapped explicitly in src/lib/resources/* — additive wire changes stay
// additive.
// Exported helpers are for contract tests only — resource modules and stores
// get the envelope strip for free inside apiFetch.

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

// Generic envelope: peels the standard { success, data } / { data } wire
// envelope down to the domain payload — and stops exactly at the envelope
// boundary.
//
// THE RULE:
//  - Peel while the current value is a record with a `data` key whose value is
//    not undefined, not null and not the object itself (max 4 layers deep).
//  - STOP as soon as a `success`-bearing layer has been consumed: after
//    peeling a layer whose object had a `success` property, do not peel again.
//    Everything below `{ success, data }` is DOMAIN payload.
//  - The legacy bare `{ data: … }` form (no `success` key) keeps peeling
//    exactly as before, so every historical wrapper still collapses to its
//    payload.
//
// THE HAZARD THIS RULE KILLS: `Notification.data` is a declared domain field
// (src/types/openapi.d.ts) carrying the notice's free-form payload — its call
// to action rides `data.actionUrl`. A notification read answers
// `{ success: true, data: { id, title, data: { actionUrl } } }`; peeling past
// the envelope silently dropped the notification's id/title and handed back
// `{ actionUrl }`. Stopping at the envelope keeps every domain field, `data`
// included — and stops a sibling field from vanishing under the fetcher.
//
// Deterministic: only peels when the `data` property exists and is neither
// undefined, null, nor a self-reference.
export function peelEnvelope<T>(raw: unknown): T {
  let cur: unknown = raw
  for (let i = 0; i < 4; i++) {
    if (!isRecord(cur)) break
    if (!('data' in cur)) break
    const d = (cur as Record<string, unknown>).data
    if (d === undefined || d === cur) break
    // Do not peel if data is null — treat as missing envelope
    if (d === null) break
    // A `{ success, data }` layer is the LAST layer to peel: everything below
    // it is domain payload, and a domain field named `data` must survive.
    const wasSuccessEnvelope = 'success' in cur
    cur = d
    if (wasSuccessEnvelope) break
  }
  return cur as T
}
